
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ConsultationSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    staff_size: '',
    current_challenges: '',
    preferred_time: '',
    urgency: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3g90n52v2m9odki63kg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          phone: '',
          staff_size: '',
          current_challenges: '',
          preferred_time: '',
          urgency: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-20 bg-gradient-to-br from-blue-600 to-teal-600 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Schedule Your Free IT Consultation</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Get a personalized assessment of your nonprofit's technology needs. Our experts will 
            provide actionable recommendations to improve efficiency and reduce costs.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl"></i>
              </div>
              <h3 className="font-semibold mb-2">Free Assessment</h3>
              <p className="text-blue-100 text-sm">No cost, no obligation technology review</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-2xl"></i>
              </div>
              <h3 className="font-semibold mb-2">Expert Guidance</h3>
              <p className="text-blue-100 text-sm">Certified professionals with nonprofit experience</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl"></i>
              </div>
              <h3 className="font-semibold mb-2">Custom Solutions</h3>
              <p className="text-blue-100 text-sm">Tailored recommendations for your organization</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form id="consultation-form" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-white/70"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-white/70"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="organization" className="block text-sm font-medium mb-2">Organization Name *</label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-white/70"
                  placeholder="Your nonprofit organization"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-white/70"
                  placeholder="(877) 596-4835"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="staff_size" className="block text-sm font-medium mb-2">Organization Size *</label>
                <div className="relative">
                  <select
                    id="staff_size"
                    name="staff_size"
                    value={formData.staff_size}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 pr-8 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white appearance-none cursor-pointer"
                  >
                    <option value="" className="text-gray-900">Select staff size</option>
                    <option value="1-5" className="text-gray-900">1-5 employees</option>
                    <option value="6-15" className="text-gray-900">6-15 employees</option>
                    <option value="16-50" className="text-gray-900">16-50 employees</option>
                    <option value="51-100" className="text-gray-900">51-100 employees</option>
                    <option value="100+" className="text-gray-900">100+ employees</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label htmlFor="urgency" className="block text-sm font-medium mb-2">Timeline *</label>
                <div className="relative">
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 pr-8 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white appearance-none cursor-pointer"
                  >
                    <option value="" className="text-gray-900">When do you need help?</option>
                    <option value="immediate" className="text-gray-900">Immediate (within 1 week)</option>
                    <option value="soon" className="text-gray-900">Soon (within 1 month)</option>
                    <option value="planning" className="text-gray-900">Planning (within 3 months)</option>
                    <option value="future" className="text-gray-900">Future consideration</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 pointer-events-none"></i>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="preferred_time" className="block text-sm font-medium mb-2">Preferred Consultation Time</label>
              <input
                type="text"
                id="preferred_time"
                name="preferred_time"
                value={formData.preferred_time}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-white/70"
                placeholder="e.g., Weekday mornings, Tuesday afternoons, etc."
              />
            </div>

            <div>
              <label htmlFor="current_challenges" className="block text-sm font-medium mb-2">Current IT Challenges *</label>
              <textarea
                id="current_challenges"
                name="current_challenges"
                value={formData.current_challenges}
                onChange={handleInputChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg focus:ring-2 focus:ring-white/50 focus:border-transparent text-white placeholder-white/70 resize-none"
                placeholder="Describe your main technology challenges, goals, or questions..."
              />
              <div className="text-sm text-blue-100 mt-1">
                {formData.current_challenges.length}/500 characters
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full bg-white text-blue-600 hover:bg-blue-50"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Scheduling Consultation...
                </>
              ) : (
                <>
                  <i className="ri-calendar-check-line mr-2"></i>
                  Schedule Free Consultation
                </>
              )}
            </Button>

            {submitStatus === 'success' && (
              <div className="bg-green-600 text-white p-4 rounded-lg">
                <i className="ri-check-line mr-2"></i>
                Thank you! We'll contact you within 24 hours to schedule your consultation.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-600 text-white p-4 rounded-lg">
                <i className="ri-error-warning-line mr-2"></i>
                Something went wrong. Please try again or call us at (877) 596-4835.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
